import { useState } from "react";
import { Compass, RotateCw } from "lucide-react";

const CYAN = "#06B6D4", AMBER = "#F59E0B", SLATE = "#64748B", STEEL = "#334155", INK = "#070B14";

const signColor = (v) => (Math.abs(v) < 0.05 ? SLATE : v > 0 ? AMBER : CYAN);
const signStr = (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}°`;

// rotate point (px,py-R) around pivot by screen-degrees (SVG positive = clockwise)
const tipPoint = (px, py, R, deg) => {
  const rad = (deg * Math.PI) / 180;
  const dx = 0, dy = -R;
  return [px + dx * Math.cos(rad) - dy * Math.sin(rad), py + dx * Math.sin(rad) + dy * Math.cos(rad)];
};

function AngleWedge({ px, py, R, screenTilt, color }) {
  const [tx, ty] = tipPoint(px, py, R, screenTilt);
  const sweep = screenTilt >= 0 ? 1 : 0;
  return (
    <path d={`M ${px} ${py} L ${px} ${py - R} A ${R} ${R} 0 0 ${sweep} ${tx} ${ty} Z`}
      fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
  );
}

function CamberSVG({ camber }) {
  const px = 110, py = 172, tilt = Math.max(-40, Math.min(40, camber * 6));
  const col = signColor(camber);
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[260px] mx-auto">
      <line x1="18" y1={py} x2="202" y2={py} stroke={STEEL} strokeWidth="2.5" strokeLinecap="round" />
      <line x1={px} y1="30" x2={px} y2={py} stroke={SLATE} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.6" />
      <AngleWedge px={px} py={py} R={56} screenTilt={tilt} color={col} />
      <g transform={`rotate(${tilt} ${px} ${py})`} style={{ transition: "transform .12s" }}>
        <rect x={px - 24} y={py - 120} width="48" height="120" rx="15" fill="#0E1526" stroke={col} strokeWidth="3" />
        <rect x={px - 15} y={py - 110} width="30" height="100" rx="9" fill={INK} stroke="#1e293b" strokeWidth="1.5" />
        {[-92, -74, -56, -38, -20].map((dy) => (
          <line key={dy} x1={px - 18} y1={py + dy} x2={px + 18} y2={py + dy} stroke={STEEL} strokeWidth="2" />
        ))}
      </g>
      <text x={px} y="22" textAnchor="middle" fill={SLATE} fontSize="10" fontFamily="monospace" letterSpacing="1">VERTICAL</text>
    </svg>
  );
}

function CasterSVG({ caster }) {
  const px = 120, py = 168, R = 46, screenTilt = -caster * 4; // +caster leans axis top toward rear (left)
  const st = Math.max(-40, Math.min(40, screenTilt));
  const col = signColor(caster);
  const [tx, ty] = tipPoint(px, py, 128, st);
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[260px] mx-auto">
      <line x1="18" y1={py} x2="202" y2={py} stroke={STEEL} strokeWidth="2.5" strokeLinecap="round" />
      <line x1={px} y1="30" x2={px} y2={py} stroke={SLATE} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.6" />
      <AngleWedge px={px} py={py} R={54} screenTilt={st} color={col} />
      {/* wheel */}
      <circle cx={px} cy={py - R} r={R} fill="#0E1526" stroke="#475569" strokeWidth="3" />
      <circle cx={px} cy={py - R} r="12" fill={INK} stroke={col} strokeWidth="2.5" />
      {/* steering axis */}
      <line x1={px} y1={py} x2={tx} y2={ty} stroke={col} strokeWidth="3" strokeLinecap="round" />
      <circle cx={px} cy={py} r="3.5" fill={col} />
      {/* direction hint */}
      <text x="196" y="44" textAnchor="end" fill={SLATE} fontSize="10" fontFamily="monospace" letterSpacing="1">FRONT →</text>
    </svg>
  );
}

const Slider = ({ value, min, max, step, onChange, testId, color }) => (
  <input type="range" min={min} max={max} step={step} value={value} data-testid={testId}
    onChange={(e) => onChange(Number(e.target.value))}
    className="w-full accent-current cursor-pointer" style={{ color }} />
);

export const CamberCasterGuide = () => {
  const [camber, setCamber] = useState(1.5);
  const [caster, setCaster] = useState(4);

  return (
    <section className="rounded-md bg-surface border border-steel p-5 sm:p-6 mb-6 animate-rise" data-testid="alignment-basics">
      <div className="flex items-center gap-2 mb-1">
        <Compass size={18} className="text-cyan" />
        <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Alignment Basics</h2>
      </div>
      <p className="text-slate-400 text-sm mb-5">Drag the sliders to see how positive (+) and negative (−) angles tilt the wheel.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Camber */}
        <div className="rounded-xl bg-ink border border-steel/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] tracking-widest uppercase text-slate-300">Camber (front view)</span>
            <span data-testid="camber-value" className="font-heading text-2xl font-black tabular-nums" style={{ color: signColor(camber) }}>{signStr(camber)}</span>
          </div>
          <CamberSVG camber={camber} />
          <Slider value={camber} min={-3} max={3} step={0.1} onChange={setCamber} testId="camber-slider" color={signColor(camber)} />
          <div className="flex justify-between font-mono text-[10px] text-slate-500 mt-1">
            <span className="text-cyan">−3° (top in)</span><span>0°</span><span className="text-amber">+3° (top out)</span>
          </div>
          <p className="text-xs text-slate-400 mt-3 leading-relaxed">
            <span className="text-amber font-semibold">Positive (+):</span> the top of the wheel leans <b>out</b>, away from the vehicle.
            <span className="text-cyan font-semibold"> Negative (−):</span> the top leans <b>in</b>, toward the vehicle — common for cornering grip.
          </p>
        </div>

        {/* Caster */}
        <div className="rounded-xl bg-ink border border-steel/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] tracking-widest uppercase text-slate-300">Caster (side view)</span>
            <span data-testid="caster-value" className="font-heading text-2xl font-black tabular-nums" style={{ color: signColor(caster) }}>{signStr(caster)}</span>
          </div>
          <CasterSVG caster={caster} />
          <Slider value={caster} min={-4} max={8} step={0.1} onChange={setCaster} testId="caster-slider" color={signColor(caster)} />
          <div className="flex justify-between font-mono text-[10px] text-slate-500 mt-1">
            <span className="text-cyan">−4° (fwd)</span><span>0°</span><span className="text-amber">+8° (rearward)</span>
          </div>
          <p className="text-xs text-slate-400 mt-3 leading-relaxed">
            <span className="text-amber font-semibold">Positive (+):</span> the steering axis tilts toward the <b>rear</b> — adds straight-line stability and steering return.
            <span className="text-cyan font-semibold"> Negative (−):</span> it tilts <b>forward</b> (rare; causes wander).
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 font-mono text-[11px] text-slate-500">
        <RotateCw size={13} className="text-cyan" /> Most vehicles run slight negative camber and positive caster. Check your Spec Vault for exact targets.
      </div>
    </section>
  );
};
